import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate, debounce, failureReason } from '../utils/helpers';

const STATUS_BADGE = {
  RESOLVED:  'badge-success',
  PENDING:   'badge-warning',
  NOT_FOUND: 'badge-info',
  ERROR:     'badge-danger',
};

/** The outcomes that have something to explain. */
const FAILED_STATUSES = ['ERROR', 'NOT_FOUND'];

const STATUS_ICON = {
  RESOLVED:  <CheckCircle2 className="w-4 h-4 text-success-600" />,
  PENDING:   <Clock        className="w-4 h-4 text-warning-600" />,
  NOT_FOUND: <XCircle      className="w-4 h-4 text-gray-400"    />,
  ERROR:     <AlertCircle  className="w-4 h-4 text-danger-600"  />,
};

const PartyLookups = () => {
  const [lookups, setLookups]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ status: '', search: '' });
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadLookups = useCallback(async (pg = page, sz = pageSize) => {
    try {
      setLoading(true);
      const data = await mojaloopApi.getPartyLookups({
        status: filters.status,
        search: filters.search,
        page: pg,
        size: sz,
      });
      setLookups(data.content    ?? []);
      setTotalItems(data.totalItems ?? 0);
      setTotalPages(data.totalPages ?? 0);
      setPage(data.page ?? 0);
    } catch (err) {
      console.error('[PartyLookups] load error:', err);
      setLookups([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(0); loadLookups(0, pageSize); }, [filters]); // eslint-disable-line
  useEffect(() => { loadLookups(page, pageSize); }, [page, pageSize]);   // eslint-disable-line

  const handleSearchChange = debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, 300);

  const handlePageChange     = (p) => setPage(p - 1);
  const handlePageSizeChange = (s) => { setPageSize(s); setPage(0); };
  const clearFilters         = () => setFilters({ status: '', search: '' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Party Lookups</h1>
          <p className="text-gray-600 mt-1">FSPIOP /parties lookup records</p>
        </div>
        <button onClick={() => loadLookups(page, pageSize)} disabled={loading} className="btn-primary flex items-center space-x-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Party ID, Correlation ID, Resolved FSP, Party Name…"
                  className="input pl-10"
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="RESOLVED">Resolved</option>
                <option value="PENDING">Pending</option>
                <option value="NOT_FOUND">Not Found</option>
                <option value="ERROR">Error</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Results{totalItems > 0 ? ` (${totalItems.toLocaleString()})` : ''}</h2>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading party lookups…</p>
              </div>
            </div>
          ) : lookups.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No party lookups found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['ID', 'Party Type', 'Party ID', 'FSP Source', 'Resolved FSP', 'Resolved Name', 'Status', 'Created'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lookups.map((lk) => (
                    <tr key={lk.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                        #{lk.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          {lk.partyType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {lk.partyId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{lk.fspSource}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {lk.resolvedFspId || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate">
                        {lk.resolvedPartyName || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1.5">
                          {STATUS_ICON[lk.status]}
                          <span className={`badge ${STATUS_BADGE[lk.status] || 'badge-info'}`}>
                            {lk.status}
                          </span>
                        </div>
                        {/* Why it ended this way: the Hub's error, or why it never got there. */}
                        {FAILED_STATUSES.includes(lk.status) && failureReason(lk.callbackPayload) && (
                          <p className="mt-1 max-w-xs truncate text-xs text-gray-500"
                             title={failureReason(lk.callbackPayload)}>
                            {failureReason(lk.callbackPayload)}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(lk.createdAt, 'PPp')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <Pagination
                currentPage={page + 1}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartyLookups;
